import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/products/{code}';
export type MakeGetProductByCodeDeps<ProductShow extends SchemaType.Top> = {
  readonly schemas: {
    readonly productShow: ProductShow;
  };
  readonly adapters: {
    readonly productShow: {
      readonly name: (product: ProductShow['Type']) => string | null;
    };
  };
  readonly rawUrlEncode: (input: string) => string;
};

export const makeGetProductByCode = <ProductShow extends SchemaType.Top>(
  deps: MakeGetProductByCodeDeps<ProductShow>
) => {
  const GetProductByCodeError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetByCode',
    path: ENDPOINT,
    identifierField: 'code',
  });

  const getProductByCodeFetch = (code: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { code: deps.rawUrlEncode(code) } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'product.code': code,
        },
      })
    );

  const getProductByCode = (code: string) =>
    getProductByCodeFetch(code).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.schemas.productShow)(response),
          404: (response) =>
            Effect.fail(new GetProductByCodeError.NotFoundError({ code, response })),
          orElse: (response) =>
            Effect.fail(new GetProductByCodeError.ApiRequestError({ code, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductGet,
          'Debug',
          `Successfully resolved product for code: ${code} -> name: ${deps.adapters.productShow.name(result) ?? 'default'}`
        )
      ),
      Effect.withSpan('api.product.get', {
        kind: 'internal',
        attributes: {
          'product.code': code,
        },
      })
    );

  return { getProductByCode, GetProductByCodeError };
};
